import { Package } from 'lucide-react';
import { formatMoney } from '../../utils/format';

/**
 * LineItemsTable - Displays standard line items included in the quote
 */
export default function LineItemsTable({ items, title = "Included Items" }) {
  if (!items || items.length === 0) {
    return null;
  }

  const formatPrice = (price) => {
    return formatMoney(parseFloat(price));
  };

  const total = items.reduce((sum, item) => {
    return sum + (parseFloat(item.unit_price) * item.quantity);
  }, 0);

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Package className="w-5 h-5 text-primary-500" />
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">{title}</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-sm text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-700">
              <th className="pb-3 pr-4">Item</th>
              <th className="pb-3 px-4 text-right">Qty</th>
              <th className="pb-3 px-4 text-right">Unit Price</th>
              <th className="pb-3 pl-4 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {items.map((item, index) => (
              <tr key={item.id || index}>
                <td className="py-3 pr-4">
                  <div className="font-medium text-neutral-900 dark:text-white">
                    {item.product_name}
                    {item.product_code && (
                      <span className="ml-2 text-xs text-neutral-500 dark:text-neutral-400">
                        ({item.product_code})
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                      {item.description}
                    </div>
                  )}
                  {item.is_recurring && (
                    <span className="inline-block mt-1 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                      Recurring {item.recurring_cycle}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-right text-neutral-900 dark:text-white">
                  {item.quantity}
                  {item.unit_of_measure && (
                    <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-1">
                      {item.unit_of_measure}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-right text-neutral-900 dark:text-white">
                  {formatPrice(item.unit_price)}
                </td>
                <td className="py-3 pl-4 text-right font-medium text-neutral-900 dark:text-white">
                  {formatPrice(parseFloat(item.unit_price) * item.quantity)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-neutral-200 dark:border-neutral-600">
              <td colSpan="3" className="pt-3 pr-4 text-right font-semibold text-neutral-900 dark:text-white">
                Subtotal:
              </td>
              <td className="pt-3 pl-4 text-right font-semibold text-neutral-900 dark:text-white">
                {formatPrice(total)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
